import React from 'react';
import '../../styles/components/AsperaReorder.scss';
import { SubManager } from '../../classes/SubManager';
import { AsperaError } from '../../classes/AsperaError';
import { Button, getIcon, getIconComponent } from '../Carbon';
import { language } from '../../services/LanguageService';
import { AsperaOverflow } from '../AsperaOverflow';
import { getRandomId } from '../../helpers/helpers';
/**
 * React Component
 *
 * The AsperaReorder component provides a reordering component.
 * Any content can be rendered inside the items.
 * An array is returned with the latest order after every change.
 * For this component order of arrays is critical.  Everything is based on order in the array.
 * If you want to update the state of your data do it on consumer side.  For example, to
 * update the disabled state after clicking edit on the options change the disabled flag on your component
 * and call forceUpdater to re-render.
 * With inPageAdd prop as true, the component no longer provides reordering capabilities.
 *
 * @remarks `localItems.map` is used to avoid sharing pointer of array and having array altered from outside of scope.
 *
 * @example
 * ```javascript
 * <AsperaReorder items={this.items} onChange={this.onChange} />
 * ```
 */
export class AsperaReorder extends React.Component {
    constructor() {
        super(...arguments);
        this.componentId = '';
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        this.dragCounter = {};
        this.draggingOverClass = 'current-dragging-over';
        this.draggingItemClass = 'current-dragging-item';
        this.subManager = new SubManager();
        this.localItems = [];
        this.dragData = {
            currentDrag: undefined,
            currentDragIndex: undefined,
        };
        this.movePosition = (item, currentIndex, down = false) => {
            let newIndex = down ? currentIndex + 1 : currentIndex - 1;
            if (newIndex < 0)
                newIndex = 0;
            if (newIndex >= this.maxIndex)
                newIndex = this.maxIndex;
            this.moveSpecificPosition(item, currentIndex, newIndex);
        };
        this.moveSpecificPosition = (item, currentIndex, newIndex) => {
            this.localItems.splice(currentIndex, 1);
            this.localItems.splice(newIndex, 0, item);
            this.handlePostMove();
        };
    }
    get maxIndex() {
        const max = this.localItems.length - 1;
        return max < 0 ? 0 : max;
    }
    handlePostMove() {
        const { onChange } = this.props;
        if (typeof onChange === 'function')
            onChange(this.localItems.map(item => item));
        this.setState({});
    }
    getCustomActions(item) {
        const actions = item.options;
        if (actions && actions.length) {
            if (actions.length <= 2) {
                return actions.map((action, index) => {
                    if (!action.icon)
                        throw new AsperaError('AsperaReorder: One or two actions provided without an icon', actions).getError();
                    return React.createElement("div", { className: "control-action", key: index },
                        React.createElement(Button, { hasIconOnly: true, size: "sm", kind: "ghost", disabled: action.disabled, iconDescription: action.itemText, tooltipAlignment: "end", tooltipPosition: "bottom", renderIcon: action.icon, onClick: () => action.onClick ? action.onClick(item) : '', "data-auto": "reorder-single-action" }));
                });
            }
            else {
                const menuItems = actions.map(action => {
                    return {
                        itemText: action.itemText,
                        disabled: action.disabled,
                        onClick: () => action.onClick ? action.onClick(item) : '',
                    };
                });
                return React.createElement("div", { className: "control-action" },
                    React.createElement(AsperaOverflow, { menuItems: menuItems, menuOptions: { flipped: true, size: 'sm' } }));
            }
        }
    }
    getChildView(item, currentIndex) {
        const { inPageAdd } = this.props;
        return (React.createElement("div", { className: "reorder-item", "data-auto": "reorder-item", draggable: !inPageAdd, onDragStart: (event) => {
                event.currentTarget.classList.add(this.draggingItemClass);
                this.dragData = { currentDrag: item, currentDragIndex: currentIndex };
            }, onDragEnd: (event) => {
                this.dragCounter = {};
                this.dragData = { currentDrag: undefined, currentDragIndex: undefined };
                event.currentTarget.classList.remove(this.draggingItemClass);
                document.querySelectorAll(`#${this.componentId} .${this.draggingOverClass}`).forEach(element => {
                    element.classList.remove(this.draggingOverClass);
                });
            } },
            !inPageAdd && React.createElement("div", { className: "reorder-item--grabber hide-on-mobile-devices" }, getIcon('Draggable')),
            React.createElement("div", { className: "reorder-item--view" }, item.component),
            React.createElement("div", { className: "reorder-item--controls" },
                !inPageAdd &&
                    React.createElement(React.Fragment, null,
                        React.createElement("div", { className: "control-action" },
                            React.createElement(Button, { "data-auto": "reorder-up-button", hasIconOnly: true, size: "sm", kind: "ghost", disabled: !currentIndex, iconDescription: language.get('reorder.moveUp'), tooltipAlignment: "end", tooltipPosition: "bottom", renderIcon: getIconComponent('ArrowUp'), onClick: () => {
                                    this.movePosition(item, currentIndex);
                                } })),
                        React.createElement("div", { className: "control-action" },
                            React.createElement(Button, { "data-auto": "reorder-down-button", hasIconOnly: true, size: "sm", kind: "ghost", disabled: currentIndex === (this.localItems.length - 1), iconDescription: language.get('reorder.moveDown'), tooltipAlignment: "end", tooltipPosition: "bottom", renderIcon: getIconComponent('ArrowDown'), onClick: () => {
                                    this.movePosition(item, currentIndex, true);
                                } }))),
                this.getCustomActions(item))));
    }
    componentWillUnmount() {
        this.subManager.unsubscribeAll();
    }
    componentDidMount() {
        const { forceUpdater, items } = this.props;
        if (forceUpdater) {
            this.subManager.setSubscription(forceUpdater.subscribe({
                next: () => {
                    this.localItems = items.map(item => item);
                    this.setState({});
                },
                error: error => {
                    new AsperaError('AsperaReorder: Encountered an error trying to watch for force updates', error);
                },
            }));
        }
        this.componentId = getRandomId();
        this.localItems = items.map(item => item);
        this.setState({});
    }
    /**
     * Check if items length has changed and restart.
     * Consumer should not alter length unless using prior sorted values.
     * This should handle deleting items if ever needed.
     */
    componentDidUpdate(previousProps) {
        const { items } = this.props;
        if (previousProps.items.length !== items.length) {
            this.localItems = items.map(item => item);
            this.setState({});
        }
    }
    /**
     * localItem has an empty object concat to add one extra drop zone at end
     */
    render() {
        const { className, light, inPageAdd } = this.props;
        return (React.createElement("div", { className: `aspera-reorder ${className ? className : ''} ${light ? 'light-mode' : ''}`, "data-auto": "reorder", id: this.componentId }, this.localItems.concat([{}]).map((item, index) => {
            return (React.createElement("div", { className: "reorder-wrapper", key: index },
                React.createElement("div", { className: "reorder-drop-area", onDragEnter: (event) => {
                        if (inPageAdd)
                            return;
                        if (!this.dragCounter[index])
                            this.dragCounter[index] = 0;
                        this.dragCounter[index]++;
                        if (this.dragData.currentDragIndex !== undefined && (this.dragData.currentDragIndex === index || (this.dragData.currentDragIndex + 1) === index))
                            return;
                        event.currentTarget.classList.add(this.draggingOverClass);
                    }, onDragLeave: (event) => {
                        if (inPageAdd)
                            return;
                        if (!this.dragCounter[index])
                            this.dragCounter[index] = 0;
                        this.dragCounter[index]--;
                        if (this.dragCounter[index] < 1) {
                            event.currentTarget.classList.remove(this.draggingOverClass);
                        }
                    }, onDragOver: (event) => {
                        event.stopPropagation();
                        event.preventDefault();
                    }, onDrop: (event) => {
                        event.stopPropagation();
                        event.preventDefault();
                        this.dragCounter = {};
                        if (this.dragData.currentDragIndex === undefined || !this.dragData.currentDrag)
                            return;
                        const flipSide = this.dragData.currentDragIndex > index;
                        this.moveSpecificPosition(this.dragData.currentDrag, this.dragData.currentDragIndex, flipSide ? index : (index - 1) < 0 ? 0 : (index - 1));
                    } }),
                !!item.component && this.getChildView(item, index)));
        })));
    }
}
